import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  User,
  UserRound,
  GraduationCap,
  Users,
  FileCheck,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";
import { useStudentAuth } from "../../context/StudentAuthContext";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Button,
  Table,
} from "../../components/ui";

const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const profileColumns = [
  {
    key: "label",
    header: "Field",
    render: (row) => (
      <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">{row.label}</span>
    ),
  },
  {
    key: "value",
    header: "Details",
    render: (row) => (
      <span className="text-xs font-medium text-neutral-800 dark:text-neutral-100">
        {row.value || <span className="text-neutral-400 dark:text-neutral-500 font-normal italic">Not provided</span>}
      </span>
    ),
  },
];

const StudentProfile = () => {
  const navigate = useNavigate();
  const { student } = useStudentAuth();

  if (!student) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950 text-neutral-500 dark:text-neutral-400 gap-2">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400 dark:text-neutral-500" />
        <span className="text-xs font-medium">Loading profile...</span>
      </div>
    );
  }

  const fullName = [student.firstName, student.middleName, student.lastName]
    .filter(Boolean)
    .join(" ");

  const GenderIcon = student.gender === "Female" ? UserRound : User;
  const documents = student.documentsSubmitted || {};

  const personalDetails = [
    { label: "Full Name", value: fullName },
    { label: "Gender", value: student.gender },
    { label: "Date of Birth", value: formatDate(student.dateOfBirth) },
    { label: "Blood Group", value: student.bloodGroup },
    { label: "Category", value: student.category },
  ];

  const academicDetails = [
    { label: "Student ID", value: student.studentId },
    { label: "Admission No", value: student.admissionNo },
    { label: "Roll Number", value: student.rollNumber },
    { label: "Class", value: student.classId?.className },
    { label: "Section", value: student.sectionId?.sectionName },
    { label: "Status", value: student.status },
    { label: "Enrolled On", value: formatDate(student.enrolledOn) },
    { label: "Transport Mode", value: student.transportMode },
  ];

  const familyDetails = [
    { label: "Father's Name", value: student.fatherName },
    { label: "Mother's Name", value: student.motherName },
    { label: "Guardian Name", value: student.guardianName },
    { label: "Primary Contact", value: student.primaryContact },
    { label: "Secondary Contact", value: student.secondaryContact },
    { label: "Email Address", value: student.email },
    { label: "Address", value: student.address },
  ];

  const docList = [
    { label: "Birth Certificate", key: "birthCertificate" },
    { label: "Transfer Certificate", key: "transferCertificate" },
    { label: "Government ID", key: "aadhaarCard" },
    { label: "Photographs", key: "photographs" },
    { label: "Report Card", key: "reportCard" },
    { label: "Other Documents", key: "other" },
  ];

  const documentColumns = [
    {
      key: "label",
      header: "Document Type",
      render: (row) => (
        <span className="text-xs font-semibold text-neutral-600 dark:text-neutral-300">{row.label}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const isSubmitted = documents[row.key];
        return isSubmitted ? (
          <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-xs">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            Submitted
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-neutral-400 dark:text-neutral-500 font-normal text-xs">
            <XCircle className="h-4 w-4 text-neutral-300 dark:text-neutral-600" />
            Pending
          </span>
        );
      },
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50/50 p-4 sm:p-6 lg:p-8 antialiased">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Navigation Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/student-home")}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            Back to Dashboard
          </Button>
        </div>

        {/* Profile Summary Card */}
        <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden">
          <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-sky-50 text-sky-600 p-4 rounded-2xl shrink-0 border border-sky-100">
                <GenderIcon className="h-8 w-8" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{fullName}</h1>
                  {student.status && (
                    <Badge variant={student.status === "ENROLLED" ? "success" : "secondary"}>
                      {student.status}
                    </Badge>
                  )}
                </div>
                <p className="text-xs font-mono text-neutral-500 dark:text-neutral-400">
                  Student ID: {student.studentId}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabular Profile Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Information Table */}
          <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl">
            <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 py-3.5 px-5">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 flex items-center gap-2">
                <User className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                Personal Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5">
              <Table columns={profileColumns} data={personalDetails} density="compact" />
            </CardContent>
          </Card>

          {/* Academic Information Table */}
          <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl">
            <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 py-3.5 px-5">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 flex items-center gap-2">
                <GraduationCap className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                Academic Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5">
              <Table columns={profileColumns} data={academicDetails} density="compact" />
            </CardContent>
          </Card>

          {/* Family Details Table */}
          <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl lg:col-span-2">
            <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 py-3.5 px-5">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 flex items-center gap-2">
                <Users className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                Parent & Guardian Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5">
              <Table columns={profileColumns} data={familyDetails} density="compact" />
            </CardContent>
          </Card>

          {/* Documents Table */}
          <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl lg:col-span-2">
            <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 py-3.5 px-5">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 flex items-center gap-2">
                <FileCheck className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                Submitted Documents
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5">
              <Table columns={documentColumns} data={docList} density="compact" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StudentProfile;